import React, { useEffect, useRef, memo } from 'react';

const TradingViewWidget = ({ symbol, theme = 'light' }) => {
  const containerRef = useRef(null);
  const scriptAddedRef = useRef(false); // Prevents adding script multiple times on re-renders

  useEffect(() => {
    const createWidget = () => {
      if (!containerRef.current || !window.TradingView) return;
      // Clear previous instance if any
      containerRef.current.innerHTML = '';

      // Pull theme-aware colors from our CSS tokens so the widget blends in.
      const readVar = (name, fallback) => {
        try {
          const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
          return v || fallback;
        } catch (_) { return fallback; }
      };
      const isDark = theme === 'dark';
      // Use --bg so the chart area matches the Risk Graph (which is transparent
      // and therefore shows the outer page background).
      const bg = readVar('--bg', isDark ? '#0d1117' : '#f7f8fa');
      const surface2 = readVar('--surface-2', isDark ? '#161c24' : '#f8fafc');
      const text = readVar('--text', isDark ? '#e6edf3' : '#1f2937');
      const gridLine = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

      new window.TradingView.widget({
        autosize: true,
        symbol: symbol || 'SPX',
        interval: 'D',
        timezone: 'Etc/UTC',
        theme: isDark ? 'dark' : 'light',
        style: '1',
        locale: 'en',
        toolbar_bg: surface2,
        enable_publishing: false,
        allow_symbol_change: true,
        container_id: containerRef.current.id,
        // Match our app palette so the widget doesn't visually clash with the rest
        overrides: {
          'paneProperties.background': bg,
          'paneProperties.backgroundType': 'solid',
          'paneProperties.backgroundGradientStartColor': bg,
          'paneProperties.backgroundGradientEndColor': bg,
          'paneProperties.vertGridProperties.color': gridLine,
          'paneProperties.horzGridProperties.color': gridLine,
          'scalesProperties.backgroundColor': bg,
          'scalesProperties.lineColor': gridLine,
          'scalesProperties.textColor': text,
        },
        loading_screen: { backgroundColor: bg, foregroundColor: text },
      });
    };

    if (window.TradingView) {
      createWidget();
      return;
    }

    if (!scriptAddedRef.current && containerRef.current) {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => createWidget();
      containerRef.current.appendChild(script);
      scriptAddedRef.current = true;
    }

    // No explicit cleanup; container removal handles iframe cleanup on unmount
  }, [symbol, theme]);

  // Ensure the container has a unique ID for the TradingView widget to target
  const widgetContainerId = `tradingview-widget-container-${symbol || 'default'}`.replace(/[^a-zA-Z0-9-_]/g, '-');

  return (
    <div 
      id={widgetContainerId} 
      ref={containerRef} 
      style={{ width: '100%', height: '100%' }} // Ensure div takes up wrapper space
    >
      {/* TradingView widget will be embedded here by the script */}
    </div>
  );
};

// Use memo to prevent re-renders if props don't change, 
// especially since the widget script handles its own lifecycle mostly.
export default memo(TradingViewWidget); 